import { CheckCircle2, TrendingUp, AlertTriangle, AlertOctagon, XCircle, MinusCircle } from 'lucide-react';

export const STATUS = {
  COMPLIANT: { label: 'Compliant', icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', ring: '#059669' },
  ON_TRACK: { label: 'On track', icon: TrendingUp, cls: 'bg-sky-50 text-sky-800 border-sky-200', ring: '#0284c7' },
  ATTENTION_REQUIRED: { label: 'Attention required', icon: AlertTriangle, cls: 'bg-amber-50 text-amber-900 border-amber-200', ring: '#d97706' },
  HIGH_RISK: { label: 'High risk', icon: AlertOctagon, cls: 'bg-orange-50 text-orange-900 border-orange-300', ring: '#ea580c' },
  NON_COMPLIANT: { label: 'Non-compliant', icon: XCircle, cls: 'bg-rose-50 text-rose-800 border-rose-200', ring: '#e11d48' },
  NOT_APPLICABLE: { label: 'Not applicable', icon: MinusCircle, cls: 'bg-slate-100 text-slate-600 border-slate-200', ring: '#94a3b8' },
};

export const CPDStatusBadge = ({ status, testId }) => {
  const s = STATUS[status] || STATUS.ATTENTION_REQUIRED;
  const Icon = s.icon;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.cls}`}>
      <Icon className="w-3.5 h-3.5" aria-hidden /> {s.label}
    </span>
  );
};

export const ProgressRing = ({ value, max, status, size = 92 }) => {
  const pct = max ? Math.min(1, value / max) : status === 'NOT_APPLICABLE' ? 0 : 1;
  const r = (size - 10) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} role="img" aria-label={`${Math.round(pct * 100)}% complete`}>
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#e2e8f0" strokeWidth="8" fill="none" />
      <circle cx={size / 2} cy={size / 2} r={r} stroke={(STATUS[status] || STATUS.ON_TRACK).ring} strokeWidth="8" fill="none"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 700ms ease' }} />
      <text x="50%" y="48%" textAnchor="middle" className="fill-foreground" style={{ fontSize: 17, fontWeight: 700 }}>{Math.round(pct * 100)}%</text>
      <text x="50%" y="66%" textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: 10 }}>{value ?? 0}/{max || '—'} h</text>
    </svg>
  );
};

export const Bar = ({ value, max, label, sub, testId }) => {
  const pct = max ? Math.min(100, (100 * value) / max) : 0;
  const met = max ? value >= max : true;
  return (
    <div data-testid={testId} className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="font-medium text-foreground">{label}</span>
        <span className={met ? 'text-emerald-700' : 'text-muted-foreground'}>{met ? '✓ ' : ''}{value ?? 0}{max ? ` / ${max}` : ''} h{sub ? ` · ${sub}` : ''}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${met ? 'bg-emerald-500' : 'bg-[#0055FF]'}`} style={{ width: `${pct}%`, transition: 'width 600ms ease' }} />
      </div>
    </div>
  );
};

export const statusLabel = (s) => STATUS[s]?.label || (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ').toLowerCase() : '');
export const humanizeEnums = (text) => text.replace(/\b(COMPLIANT|ON_TRACK|ATTENTION_REQUIRED|HIGH_RISK|NON_COMPLIANT|NOT_APPLICABLE)\b/g, (m) => STATUS[m].label);
